/**
 * Presentation logic for the global offline banner.
 *
 * Split from services/connectivity.ts on purpose: that module owns the *truth*
 * (is the API reachable?), this one owns the *UX* (should we say so, and when).
 * The delay matters — connectivity blips are common on mobile, and a banner
 * that flashes on every tunnel is worse than no banner.
 */
import { readonly, ref, watch, type Ref } from 'vue';
import { isOffline } from '@/services/connectivity';
import { useToast } from '@/composables/useToast';

/** Ride out brief drops before telling the user anything. */
const SHOW_DELAY_MS = 1500;

const active = ref(false);
let timer: ReturnType<typeof setTimeout> | undefined;
let started = false;

/** True when the offline banner should be on screen. */
export const offlineBannerActive: Readonly<Ref<boolean>> = readonly(active);

/** Idempotent; called once from main.ts. */
export function initOfflineUi(): void {
  if (started) return;
  started = true;

  watch(
    isOffline,
    (off) => {
      clearTimeout(timer);

      if (off) {
        timer = setTimeout(() => {
          active.value = true;
        }, SHOW_DELAY_MS);
        return;
      }

      // Only celebrate coming back if we actually announced the outage.
      const wasShown = active.value;
      active.value = false;
      if (wasShown) void showBackOnline();
    },
    { immediate: true },
  );
}

async function showBackOnline(): Promise<void> {
  try {
    await useToast().success("You're back online.");
  } catch {
    /* no-op — a missed toast must never break recovery */
  }
}
